import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';

// ── Self-service ─────────────────────────────────────────────────────

export interface BillingBundle {
  billing: {
    uid: string;
    mode: 'subscription' | 'payg' | null;
    planId: string | null;
    status: 'pending' | 'active' | 'past_due' | 'paused' | 'cancelled';
    creditCents: number;
    currency: 'EUR';
    autoTopUpEnabled: boolean;
    autoTopUpThresholdCents: number;
    autoTopUpAmountCents: number;
    subscriptionMonthlyMinutes: number;
    subscriptionMonthlyUsedMinutes: number;
    subscriptionResetAtMs: number | null;
    subscriptionRenewalAtMs: number | null;
    mollieCustomerId: string | null;
    mollieMandateId: string | null;
    mollieSubscriptionId: string | null;
    paymentMethod: { brand?: string; last4?: string; method?: string } | null;
    blockedSinceMs: number | null;
    /** Set when user clicked "cancel at end of period" — status stays
     *  `'active'` until the daily flipper cron rolls past this ms. The
     *  UI shows a "wird zum X gekündigt"-Badge while this is non-null. */
    pendingCancelAtMs: number | null;
    activatedAt: { toDate?: () => Date; _seconds?: number } | null;
    lastChargeAt: { toDate?: () => Date; _seconds?: number } | null;
    cancelledAt: { toDate?: () => Date; _seconds?: number } | null;
    updatedAt: { toDate?: () => Date; _seconds?: number };
  };
  plans: {
    subscription: {
      id: string;
      label: string;
      monthlyPriceNetCents: number;
      monthlyIncludedMinutes: number;
      overagePerMinuteNetCents: number;
    };
    payg: {
      id: string;
      label: string;
      perMinuteNetCents: number;
      defaultTopUpNetCents: number;
      minTopUpNetCents: number;
    };
  };
  address: {
    country: string;
    vatId: string;
    companyName: string;
    line1: string;
    line2: string;
    city: string;
    postalCode: string;
    state: string;
  } | null;
  tax: { rate: number; country: string; reverseCharge: boolean };
  pricesGross: {
    subscriptionMonthlyCents: number;
    paygPerMinuteCents: number;
    paygDefaultTopUpCents: number;
    paygMinTopUpCents: number;
  };
  settingsMode: 'beginner' | 'advanced' | null;
  assignedNumber: {
    id: string;
    e164: string;
    provider: 'twilio' | 'telnyx';
    countryCode: string;
  } | null;
}

export function useMyBilling() {
  const { user } = useUser();
  return useQuery<BillingBundle>({
    queryKey: ['my-billing', user?.uid] as const,
    enabled: !!user,
    staleTime: 15_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, BillingBundle>(functions, 'getMyBilling');
      const r = await fn({});
      return r.data;
    },
  });
}

export interface CreditTransactionRow {
  id: string;
  ts: string | null;
  type: string;
  amountCents: number;
  balanceAfterCents: number;
  currency: 'EUR';
  reason: string;
  vatRate: number | null;
  vatCents: number | null;
  netCents: number | null;
  mollie: { paymentId?: string; subscriptionId?: string; method?: string } | null;
  callId: string | null;
  minutesBilled: number | null;
  createdBy: string;
  reconciled: boolean;
}

export function useMyCreditTransactions(limit = 50) {
  const { user } = useUser();
  return useQuery<{ items: CreditTransactionRow[] }>({
    queryKey: ['my-credit-transactions', user?.uid, limit] as const,
    enabled: !!user,
    staleTime: 15_000,
    queryFn: async () => {
      const fn = httpsCallable<{ limit: number }, { items: CreditTransactionRow[] }>(
        functions,
        'listMyCreditTransactions',
      );
      const r = await fn({ limit });
      return r.data;
    },
  });
}

export function useSetSettingsMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mode: 'beginner' | 'advanced') => {
      const fn = httpsCallable<{ mode: 'beginner' | 'advanced' }, { ok: true; mode: string }>(
        functions,
        'setMySettingsMode',
      );
      const r = await fn({ mode });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-billing'] });
      qc.invalidateQueries({ queryKey: ['ui-prefs'] });
    },
  });
}

export function useStartCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      mode: 'subscription' | 'payg';
      topUpAmountCents?: number;
    }) => {
      const fn = httpsCallable<typeof input, { checkoutUrl: string; paymentId: string }>(
        functions,
        'startMollieCheckout',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-billing'] }),
  });
}

export interface PaymentMethodRow {
  id: string;
  status: 'valid' | 'pending' | 'invalid';
  method: string | null;
  cardLabel: string | null;
  cardNumber: string | null;
  cardExpiryDate: string | null;
}

export function useMyPaymentMethods() {
  const { user } = useUser();
  return useQuery<{ items: PaymentMethodRow[] }>({
    queryKey: ['my-payment-methods', user?.uid] as const,
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { items: PaymentMethodRow[] }>(
        functions,
        'listMyPaymentMethods',
      );
      const r = await fn({});
      return r.data;
    },
  });
}

export function useSetPrimaryPaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mandateId: string) => {
      const fn = httpsCallable<{ mandateId: string }, { ok: true }>(
        functions,
        'setPrimaryPaymentMethod',
      );
      const r = await fn({ mandateId });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-billing'] });
      qc.invalidateQueries({ queryKey: ['my-payment-methods'] });
    },
  });
}

export function useRemovePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mandateId: string) => {
      const fn = httpsCallable<{ mandateId: string }, { ok: true }>(
        functions,
        'removePaymentMethod',
      );
      const r = await fn({ mandateId });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-billing'] });
      qc.invalidateQueries({ queryKey: ['my-payment-methods'] });
    },
  });
}

/**
 * Computes alerts the user should act on:
 *   - expired payment methods
 *   - payment methods expiring this month
 *
 * Drives the red-circle badge in the sidebar `/usage` row, and the
 * inline expiry markers in the PaymentMethodsCard.
 */
export interface PaymentMethodAlerts {
  expiredCount: number;
  expiringSoonCount: number;
  total: number;
}

export function paymentMethodExpiryStatus(
  cardExpiryDate: string | null,
): 'expired' | 'expiring_soon' | 'ok' | 'unknown' {
  if (!cardExpiryDate) return 'unknown';
  const exp = new Date(cardExpiryDate);
  if (Number.isNaN(exp.getTime())) return 'unknown';
  // Cards typically expire at the END of the printed month — convert
  // a 2026-12-15 to 2026-12-31 23:59 to be safe before flagging expired.
  const lastOfMonth = new Date(
    exp.getFullYear(),
    exp.getMonth() + 1,
    0,
    23,
    59,
    59,
  );
  const now = new Date();
  if (now > lastOfMonth) return 'expired';
  // "Expiring soon" = same calendar month
  if (
    exp.getFullYear() === now.getFullYear() &&
    exp.getMonth() === now.getMonth()
  ) {
    return 'expiring_soon';
  }
  return 'ok';
}

export function useMyAlerts(): PaymentMethodAlerts {
  const methods = useMyPaymentMethods();
  return React.useMemo(() => {
    let expired = 0;
    let soon = 0;
    for (const m of methods.data?.items ?? []) {
      const s = paymentMethodExpiryStatus(m.cardExpiryDate);
      if (s === 'expired' && m.status === 'valid') expired += 1;
      else if (s === 'expiring_soon' && m.status === 'valid') soon += 1;
    }
    return {
      expiredCount: expired,
      expiringSoonCount: soon,
      total: expired + soon,
    };
  }, [methods.data]);
}

export function useTriggerManualTopUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (amountCents: number) => {
      const fn = httpsCallable<{ amountCents: number }, { paymentId: string; status: string }>(
        functions,
        'triggerManualTopUp',
      );
      const r = await fn({ amountCents });
      return r.data;
    },
    onSuccess: () => {
      // Mollie webhook lands a few seconds AFTER the off-session charge
      // succeeds; an immediate invalidation often returns the old wallet
      // balance. Schedule extra refetches on a sliding window so the UI
      // updates as soon as the webhook has reconciled — the user doesn't
      // have to refresh manually.
      const invalidate = () => {
        qc.invalidateQueries({ queryKey: ['my-billing'] });
        qc.invalidateQueries({ queryKey: ['my-credit-transactions'] });
        qc.invalidateQueries({ queryKey: ['my-invoices'] });
      };
      invalidate();
      [1500, 4000, 9000, 15_000].forEach((delay) =>
        window.setTimeout(invalidate, delay),
      );
    },
  });
}

export function useSetAutoTopUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { enabled: boolean; thresholdCents: number; amountCents: number }) => {
      const fn = httpsCallable<typeof input, { ok: true }>(functions, 'setMyAutoTopUp');
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-billing'] }),
  });
}

export function useSetMyBillingAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      country: string;
      vatId?: string;
      companyName?: string;
      line1?: string;
      line2?: string;
      city?: string;
      postalCode?: string;
      state?: string;
    }) => {
      const fn = httpsCallable<typeof input, { ok: true }>(
        functions,
        'setMyBillingAddress',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-billing'] }),
  });
}

export function useSetSpendCap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (capCents: number | null) => {
      const fn = httpsCallable<{ capCents: number | null }, { ok: true }>(
        functions,
        'setMySpendCap',
      );
      const r = await fn({ capCents });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-billing'] }),
  });
}

export function useAdminRefundCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { uid: string; callId: string; reason?: string }) => {
      const fn = httpsCallable<
        typeof input,
        { ok: true; balanceAfterCents: number; refundedCents: number }
      >(functions, 'adminRefundCall');
      const r = await fn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin-user-billing', vars.uid] });
      void qc.invalidateQueries({ queryKey: ['admin-user-usage', vars.uid] });
    },
  });
}

export function useAdminSwitchUserPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      uid: string;
      target: 'subscription' | 'payg' | 'enterprise' | 'ultimate' | 'cancel';
      reason?: string;
    }) => {
      const fn = httpsCallable<typeof input, { ok: true; target: string }>(
        functions,
        'adminSwitchUserPlan',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin-user-billing', vars.uid] });
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export interface ReconciliationRow {
  id: string;
  uid?: string | null;
  path: string;
  mollieSubscriptionId?: string;
  mollieCustomerId?: string;
  localStatus?: string;
  remoteStatus?: string;
  ts: string | null;
  resolved?: boolean;
  resolveNote?: string | null;
  details?: Record<string, unknown>;
}

export function useAdminReconciliation() {
  const { user } = useUser();
  return useQuery<ReconciliationRow[]>({
    queryKey: ['admin-reconciliation'] as const,
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { items: ReconciliationRow[] }>(
        functions,
        'adminListReconciliation',
      );
      const r = await fn({});
      return r.data.items;
    },
  });
}

export function useAdminResolveReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; note?: string }) => {
      const fn = httpsCallable<typeof input, { ok: true }>(
        functions,
        'adminResolveReconciliation',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-reconciliation'] }),
  });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mode: 'end_of_period' | 'immediate' = 'end_of_period') => {
      const fn = httpsCallable<
        { mode: 'end_of_period' | 'immediate' },
        { ok: true; mode: string; cancelAtMs?: number }
      >(functions, 'cancelMySubscription');
      const r = await fn({ mode });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-billing'] }),
  });
}

export function useSwitchPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (target: 'subscription' | 'payg') => {
      const fn = httpsCallable<
        { target: 'subscription' | 'payg' },
        {
          ok: true;
          switched: boolean;
          /** True when the first month was billed from wallet credit
           *  instead of via Mollie checkout — no redirect needed. */
          paidFromWallet?: boolean;
          checkoutUrl?: string;
          paymentId?: string;
          message?: string;
          alreadyOnTarget?: boolean;
        }
      >(functions, 'switchAnfaengerPlan');
      const r = await fn({ target });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-billing'] });
      qc.invalidateQueries({ queryKey: ['my-credit-transactions'] });
      qc.invalidateQueries({ queryKey: ['my-invoices'] });
    },
  });
}

export interface AdvancedAccessDTO {
  uid: string;
  status: 'inactive' | 'pending' | 'active' | 'cancelled' | 'past_due';
  /** Tier discriminator written by the Mollie webhook on first
   *  payment. Older docs may omit it — treat undefined as 'enterprise'. */
  tier?: 'enterprise' | 'ultimate';
  mollieCustomerId: string | null;
  mollieMandateId: string | null;
  mollieSubscriptionId: string | null;
  monthlyGrossCents: number | null;
  activatedAt: string | null;
  cancelledAt: string | null;
  nextRenewalAtMs: number | null;
}

export function useMyAdvancedAccess() {
  const { user } = useUser();
  return useQuery<{
    access: AdvancedAccessDTO;
    pricing: { monthlyGrossCents: number };
  }>({
    queryKey: ['my-advanced-access', user?.uid] as const,
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { access: AdvancedAccessDTO; pricing: { monthlyGrossCents: number } }>(
        functions,
        'getMyAdvancedAccess',
      );
      const r = await fn({});
      return r.data;
    },
  });
}

export function useStartAdvancedCheckout() {
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable<unknown, { checkoutUrl: string; paymentId: string }>(
        functions,
        'startAdvancedCheckout',
      );
      const r = await fn({});
      return r.data;
    },
  });
}

export function useStartUltimateCheckout() {
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable<unknown, { checkoutUrl: string; paymentId: string }>(
        functions,
        'startUltimateCheckout',
      );
      const r = await fn({});
      return r.data;
    },
  });
}

export function useCancelAdvancedSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable<unknown, { ok: true }>(
        functions,
        'cancelAdvancedSubscription',
      );
      const r = await fn({});
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-advanced-access'] }),
  });
}

// ── Admin: number pool ───────────────────────────────────────────────

export interface NumberPoolRow {
  id: string;
  e164: string;
  provider: 'twilio' | 'telnyx';
  providerSid: string;
  countryCode: string;
  monthlyFeeCents: number;
  status: 'available' | 'reserved' | 'assigned' | 'released' | 'failed';
  assignedToUid: string | null;
  assignedUserEmail: string | null;
  assignedUserName: string | null;
  notes: string;
  configuredWebhookUrl: string | null;
  purchasedAt: string | null;
  assignedAt: string | null;
}

export function useNumberPool() {
  const { isAdmin } = useUser();
  return useQuery<{ items: NumberPoolRow[]; summary: Record<string, number> }>({
    queryKey: ['admin-number-pool'] as const,
    enabled: isAdmin,
    staleTime: 15_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { items: NumberPoolRow[]; summary: Record<string, number> }>(
        functions,
        'adminListNumberPool',
      );
      const r = await fn({});
      return r.data;
    },
  });
}

export interface AvailableNumberRow {
  phoneNumber: string;
  friendlyName?: string;
  locality?: string;
  region?: string;
  isoCountry?: string;
  capabilities?: unknown;
}

export function useSearchPlatformNumbers() {
  return useMutation({
    mutationFn: async (input: {
      provider: 'twilio' | 'telnyx';
      countryCode: string;
      areaCode?: string;
      contains?: string;
      limit?: number;
    }) => {
      const fn = httpsCallable<typeof input, { items: AvailableNumberRow[] }>(
        functions,
        'adminSearchPlatformNumbers',
      );
      const r = await fn(input);
      return r.data;
    },
  });
}

export function useBuyPoolNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      provider: 'twilio' | 'telnyx';
      phoneNumber: string;
      countryCode: string;
      monthlyFeeCents?: number;
      notes?: string;
    }) => {
      const fn = httpsCallable<typeof input, { ok: true; id: string }>(
        functions,
        'adminBuyPoolNumber',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-number-pool'] }),
  });
}

export function useReleasePoolNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const fn = httpsCallable<{ id: string }, { ok: true }>(functions, 'adminReleasePoolNumber');
      const r = await fn({ id });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-number-pool'] }),
  });
}

export function useAssignPoolNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; uid: string }) => {
      const fn = httpsCallable<typeof input, { ok: true }>(functions, 'adminAssignPoolNumber');
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-number-pool'] }),
  });
}

export function useUnassignPoolNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const fn = httpsCallable<{ id: string }, { ok: true }>(functions, 'adminUnassignPoolNumber');
      const r = await fn({ id });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-number-pool'] }),
  });
}

// ── Admin: platform config (keys + pricing + tax) ────────────────────

export interface PlatformConfig {
  status: {
    mollie: { hasApiKey: boolean; mode: 'test' | 'live'; hasWebhookSecret: boolean; profileId: string | null };
    openai: { hasApiKey: boolean };
    anthropic: { hasApiKey: boolean };
    google: { hasApiKey: boolean };
    groq: { hasApiKey: boolean };
    elevenlabs: { hasApiKey: boolean };
    deepgram: { hasApiKey: boolean };
    twilio: { hasAccountSid: boolean; hasAuthToken: boolean };
    telnyx: { hasApiKey: boolean; defaultConnectionId: string | null };
    updatedAt: unknown;
  };
  pricingPlans: BillingBundle['plans'];
  taxRates: {
    homeCountry: string;
    homeRate: number;
    defaultRate: number;
    ratesByCountry: Record<string, number>;
  };
}

export function usePlatformConfig() {
  const { isAdmin } = useUser();
  return useQuery<PlatformConfig>({
    queryKey: ['admin-platform-config'] as const,
    enabled: isAdmin,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, PlatformConfig>(
        functions,
        'adminGetPlatformConfig',
      );
      const r = await fn({});
      return r.data;
    },
  });
}

export function useSetPlatformSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; value: string }) => {
      const fn = httpsCallable<typeof input, { ok: true }>(functions, 'adminSetPlatformSecret');
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-platform-config'] }),
  });
}

/** Seeds the two Anfänger plans into /membershipPlans if they don't yet
 *  exist. Idempotent — admin can click without fear of overwriting edits. */
export function useSeedAnfaengerPlans() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable<unknown, { ok: true; created: { subscription: boolean; payg: boolean } }>(
        functions,
        'adminSeedAnfaengerPlans',
      );
      const r = await fn({});
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-membership-plans'] });
      qc.invalidateQueries({ queryKey: ['admin-platform-config'] });
    },
  });
}

export function useUpdatePlatformTaxRates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PlatformConfig['taxRates']) => {
      const fn = httpsCallable<typeof input, { ok: true }>(
        functions,
        'adminUpdatePlatformTaxRates',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-platform-config'] }),
  });
}

// ── Admin: billing oversight ─────────────────────────────────────────

export interface AdminBillingRow {
  uid: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  role: string;
  billing: Record<string, unknown> & {
    mode: 'subscription' | 'payg' | null;
    status: string;
    creditCents: number;
    planId: string | null;
    mollieCustomerId: string | null;
    mollieSubscriptionId: string | null;
  };
}

export function useAdminBilling() {
  const { isAdmin } = useUser();
  return useQuery<{
    items: AdminBillingRow[];
    totals: { users: number; activeUsers: number; subscriptionUsers: number; paygUsers: number; totalCreditCents: number };
  }>({
    queryKey: ['admin-billing-list'] as const,
    enabled: isAdmin,
    staleTime: 15_000,
    queryFn: async () => {
      const fn = httpsCallable<
        unknown,
        {
          items: AdminBillingRow[];
          totals: { users: number; activeUsers: number; subscriptionUsers: number; paygUsers: number; totalCreditCents: number };
        }
      >(functions, 'adminListBilling');
      const r = await fn({});
      return r.data;
    },
  });
}

export function useAdminUserBilling(uid: string | undefined) {
  const { isAdmin } = useUser();
  return useQuery<{
    user: {
      uid: string;
      email: string;
      displayName: string;
      firstName: string;
      lastName: string;
      role: string;
    };
    billing: Record<string, unknown>;
    address: Record<string, unknown> | null;
    transactions: CreditTransactionRow[];
    assignedNumbers: Array<{
      id: string;
      e164: string;
      provider: 'twilio' | 'telnyx';
      status: string;
      assignedAt: string | null;
    }>;
  }>({
    queryKey: ['admin-billing-user', uid] as const,
    enabled: isAdmin && !!uid,
    staleTime: 15_000,
    queryFn: async () => {
      const fn = httpsCallable<{ uid: string }, never>(functions, 'adminGetUserBilling');
      const r = await fn({ uid: uid! });
      return r.data as never;
    },
  });
}

export function useAdjustCredits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      uid: string;
      amountCents: number;
      reason: string;
      type?: 'admin_adjustment' | 'signup_bonus' | 'refund';
    }) => {
      const fn = httpsCallable<typeof input, { ok: true; balanceAfterCents: number }>(
        functions,
        'adminAdjustCredits',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-billing-list'] });
      qc.invalidateQueries({ queryKey: ['admin-billing-user'] });
    },
  });
}

// ── Helpers ──────────────────────────────────────────────────────────

export function formatEUR(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
